/* Shared API plumbing for the line-item report screens (GRN datewise, sales
 * datewise, …) that all hang off /pharmacy-ims/api/v1/reports.
 *
 * Three things are genuinely common to them and live here so they stay in one
 * place: the response envelope, the 45-day date window every item-level report
 * enforces, and the async export task endpoints (which are report-type-agnostic
 * on the backend — POST <report>/export starts a task, then everyone polls and
 * downloads through v1/reports/tasks/{taskId}). */
import moment from "moment";
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";
import { saveAs } from "file-saver";

/* Audit header — resolved per call to the signed-in user. */
export const xUser = () => ({ "X-User": getCurrentUserName() });

const DATE_FMT = "YYYY-MM-DD";

const TASKS = "v1/reports/tasks";

/* Longest date range a single item-level report request may span, inclusive of
   both ends. The backend rejects anything wider with a 400 rather than trimming
   it, so the screens validate before firing. Mirrors the backend's
   ReportDateWindow.MAX_WINDOW_DAYS. */
export const MAX_WINDOW_DAYS = 45;

/* The backend's own default window (most recent 45 days) made explicit, so a
   screen shows the range it is actually reporting on. */
export const defaultRange = () => ({
  fromDate: moment().subtract(MAX_WINDOW_DAYS - 1, "days").format(DATE_FMT),
  toDate: moment().format(DATE_FMT),
});

/* null when the range is acceptable, else the message to surface. Both bounds
   blank is fine — the backend then defaults to the most recent 45 days. */
export const rangeError = (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  const from = moment(fromDate, DATE_FMT);
  const to = moment(toDate, DATE_FMT);
  if (!from.isValid() || !to.isValid()) return null;
  if (from.isAfter(to)) return "Start date must not be after the end date.";
  const days = to.diff(from, "days") + 1;
  if (days > MAX_WINDOW_DAYS) {
    return `Date range must not exceed ${MAX_WINDOW_DAYS} days — this one spans ${days}.`;
  }
  return null;
};

/* Unwrap the { success, message, data, timestamp } envelope, throwing the
   backend's message when success === false. */
export const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Re-throw an axios failure carrying the backend's own message. Error responses
   use the same envelope ({ success: false, message }) — a 400 here is usually the
   45-day window rule, which is worth showing verbatim rather than as
   "Request failed with status code 400". A blob response (download) has no
   readable body, so it falls through to the axios message. */
export const rethrow = (e) => {
  const msg = e?.response?.data?.message;
  throw typeof msg === "string" && msg ? new Error(msg) : e;
};

/* Normalize an async-export task envelope payload:
   { taskId, reportType, status, fileName, rowCount, errorMessage, ... }
   -> { taskId, status: PENDING | READY | FAILED, raw, fileName, rowCount, message }. */
export const normalizeTask = (data = {}) => {
  const raw = (data.status ?? data.state ?? "").toString().toUpperCase();
  let status = "PENDING";
  if (["READY", "COMPLETED", "COMPLETE", "SUCCESS", "DONE"].includes(raw)) status = "READY";
  else if (["FAILED", "FAILURE", "ERROR"].includes(raw)) status = "FAILED";
  return {
    taskId: data.taskId ?? data.id ?? data.reportId ?? null,
    status,
    raw,
    fileName: data.fileName ?? data.filename ?? null,
    rowCount: data.rowCount ?? null,
    message: data.errorMessage ?? data.message ?? null,
  };
};

/* Report-type-agnostic task endpoints — every report's export polls and
   downloads through these. */
export const reportTasks = {
  /* GET v1/reports/tasks/{taskId} -> the task object, polled until COMPLETED / FAILED. */
  status(taskId) {
    return httpSupplier
      .get(`${TASKS}/${taskId}`, { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)))
      .catch(rethrow);
  },

  /* GET v1/reports/tasks/{taskId}/download -> CSV blob; saves to disk. */
  download(taskId, filename = "report.csv") {
    return httpSupplier
      .get(`${TASKS}/${taskId}/download`, { responseType: "blob", headers: xUser() })
      .then((r) => {
        saveAs(new Blob([r.data], { type: "text/csv;charset=utf-8" }), filename);
      });
  },
};

/* Coerce a nullable numeric field to a plain number (BigDecimals arrive as
   JSON numbers, but nulls are common on optional columns). */
export const num = (v) => Number(v ?? 0) || 0;
